/**
 * Recommendations Hook
 *
 * TanStack Query hook for fetching recommendations and rating them.
 * Uses optimistic updates for rating mutations — the UI reflects
 * the new rating immediately, rolling back on failure.
 *
 * This hook is for real API mode. Demo mode uses local state in
 * useDemoPipeline instead.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  RecommendationListResponse,
  RecommendationRating,
} from "../types/recommendation";
import {
  getRecommendations,
  rateRecommendation,
} from "../services/recommendations";

/**
 * Fetch recommendations for a note and provide a rating mutation.
 *
 * @param noteId - UUID of the note (undefined while loading)
 * @returns Query result + rate mutation function
 */
export function useRecommendations(noteId: string | undefined) {
  const queryClient = useQueryClient();
  const queryKey = ["recommendations", noteId];

  const query = useQuery({
    queryKey,
    queryFn: () => getRecommendations(noteId!),
    enabled: !!noteId,
    // Recommendations rarely change once generated — stale after 60s
    staleTime: 60_000,
  });

  const rateMutation = useMutation({
    mutationFn: ({
      recId,
      rating,
    }: {
      recId: string;
      rating: RecommendationRating;
    }) => rateRecommendation(recId, rating),

    // Optimistic update: apply rating to cache immediately
    onMutate: async ({ recId, rating }) => {
      // Cancel any in-flight refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey });

      // Snapshot current cache for rollback
      const previousData =
        queryClient.getQueryData<RecommendationListResponse>(queryKey);

      // Optimistically update the cached recommendation
      queryClient.setQueryData<RecommendationListResponse>(queryKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          items: old.items.map((rec) =>
            rec.id === recId
              ? { ...rec, rating, ratedAt: new Date().toISOString() }
              : rec,
          ),
        };
      });

      return { previousData };
    },

    // On error, rollback to the snapshot
    onError: (_err, _vars, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },

    // After mutation settles (success or error), refetch for consistency
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    recommendations: query.data?.items ?? [],
    total: query.data?.total ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    rate: (recId: string, rating: RecommendationRating) =>
      rateMutation.mutate({ recId, rating }),
    isRating: rateMutation.isPending,
  };
}
